import {
  MawpCalcThicknessOption,
  MawpCalcType,
} from 'features/indicationTriage/cards/ffsCalc/FfsSharedValues'
import { FfsLtaReport } from 'features/indicationTriage/cards/ffsCalc/ffsStrategyContext/ffsReports'
import {
  ScenarioValues,
  useFfsStrategy,
} from 'features/indicationTriage/cards/ffsCalc/ffsStrategyContext/FfsStrategyContext'
import { ValueSetterPair } from 'features/indicationTriage/cards/ffsCalc/typeUtils'
import { useCallback, useEffect, useRef } from 'react'
import { GeckoMeasurement } from 'types'

import { FfsLevel, FfsPart } from './ffsTypes'

export type UseFfsScenariosProps = ValueSetterPair<
  string | null,
  'selectedScenarioId'
> &
  ValueSetterPair<GeckoMeasurement | null, 'rsf_a'> &
  ValueSetterPair<GeckoMeasurement | null, 'e'> &
  ValueSetterPair<GeckoMeasurement | null, 'y'> &
  ValueSetterPair<GeckoMeasurement | null, 'w'> &
  ValueSetterPair<GeckoMeasurement | null, 's'> &
  ValueSetterPair<GeckoMeasurement | null, 'tsl'> &
  ValueSetterPair<GeckoMeasurement | null, 'fca'> &
  ValueSetterPair<GeckoMeasurement | null, 'fca_ml'> &
  ValueSetterPair<MawpCalcThicknessOption, 'selectedMawpCalcThickness'> &
  ValueSetterPair<MawpCalcType | null, 'calcType'> & {
    scenarios: ReadonlyArray<FfsLtaReport>
    ffsLevel: FfsLevel
    ffsPart: FfsPart
  }

function areMeasurementsEqual(
  meas_a: GeckoMeasurement | null | undefined,
  meas_b: GeckoMeasurement | null | undefined
): boolean {
  // Handle null/undefined cases
  if (meas_a === meas_b) return true
  if (!meas_a) return !meas_b
  if (!meas_b) return !meas_a

  // Both measurements exist, use GeckoMeasurement.Equivalent for unit-aware comparison
  return GeckoMeasurement.Equivalent(meas_a, meas_b)
}

// Helper to compare all scenario values with current state
function compareScenarioValues(
  currentValues: ScenarioValues,
  scenarioValues: ScenarioValues
): boolean {
  return (
    areMeasurementsEqual(currentValues.rsf_a, scenarioValues.rsf_a) &&
    areMeasurementsEqual(currentValues.e, scenarioValues.e) &&
    areMeasurementsEqual(currentValues.y, scenarioValues.y) &&
    areMeasurementsEqual(currentValues.w, scenarioValues.w) &&
    areMeasurementsEqual(currentValues.s_y, scenarioValues.s_y) &&
    areMeasurementsEqual(currentValues.tsl, scenarioValues.tsl) &&
    areMeasurementsEqual(currentValues.fca, scenarioValues.fca) &&
    areMeasurementsEqual(currentValues.fcaMl, scenarioValues.fcaMl) &&
    currentValues.selectedMawpCalcThickness ===
      scenarioValues.selectedMawpCalcThickness &&
    currentValues.calcType === scenarioValues.calcType
  )
}

// Hook which sets the current scenario to the default scenario for the given FFS level and part
// Fires only once when we first load the scenarios
function useDefaultScenarioOnce(
  scenarios: ReadonlyArray<FfsLtaReport>,
  ffsLevel: FfsLevel,
  scenarioStrategy: ReturnType<typeof useFfsStrategy>['strategy']['scenarios'],
  setSelectedScenarioId: (id: string | null) => void
): void {
  const latch = useRef(false)
  useEffect(() => {
    if (!latch.current) {
      const defaultScenario = scenarios.find((scenario) =>
        scenarioStrategy.isDefaultScenario({ scenario, ffsLevel })
      )
      if (defaultScenario) {
        setSelectedScenarioId(defaultScenario.id)
        latch.current = true
      }
    }
  }, [scenarios, ffsLevel, scenarioStrategy, setSelectedScenarioId])
}

// Get the last value of a state, and set the new value
// Used to see if the scenario changed since the last effect firing
function useGetAndSet<T>(initialValue: T): (newValue: T) => T {
  const lastValue = useRef(initialValue)
  return (newValue) => {
    const ret = lastValue.current
    lastValue.current = newValue
    return ret
  }
}

// Ties the FFS values to the selected scenario
// Sets the default scenario on load
// Clears the current scenario selection if FFS values are changed independently
export function useFfsScenarios(props: UseFfsScenariosProps): void {
  const {
    strategy: { scenarios: scenarioStrategy },
  } = useFfsStrategy()
  const {
    selectedScenarioId,
    setSelectedScenarioId,
    rsf_a,
    setRsf_a,
    e,
    setE,
    y,
    setY,
    w,
    setW,
    s,
    setS,
    tsl,
    setTsl,
    fca,
    setFca,
    fca_ml,
    setFca_ml,
    selectedMawpCalcThickness,
    setSelectedMawpCalcThickness,
    calcType,
    setCalcType,
    scenarios,
    ffsLevel,
  } = props

  // On initial load, find and set the default scenario for the current level and part
  useDefaultScenarioOnce(
    scenarios,
    ffsLevel,
    scenarioStrategy,
    setSelectedScenarioId
  )

  const setLastSelectedScenarioId = useGetAndSet<string | null>(null)

  const setScenarioValues = useCallback(
    (scenario: FfsLtaReport) => {
      const values = scenarioStrategy.getScenarioValues(scenario)
      setRsf_a(values.rsf_a)
      setE(values.e)
      setY(values.y)
      setW(values.w)
      setS(values.s_y)
      setTsl(values.tsl)
      setFca(values.fca)
      setFca_ml(values.fcaMl)
      setSelectedMawpCalcThickness(values.selectedMawpCalcThickness)
      setCalcType(values.calcType)
    },
    [
      scenarioStrategy,
      setRsf_a,
      setE,
      setY,
      setW,
      setS,
      setTsl,
      setFca,
      setFca_ml,
      setSelectedMawpCalcThickness,
      setCalcType,
    ]
  )

  const doesStateMatchScenario = useCallback(
    (scenario: FfsLtaReport | undefined) => {
      if (!scenario) return false

      const currentValues: ScenarioValues = {
        rsf_a,
        e,
        y,
        w,
        s_y: s,
        tsl,
        fca,
        fcaMl: fca_ml,
        selectedMawpCalcThickness,
        calcType,
      }

      const scenarioValues = scenarioStrategy.getScenarioValues(scenario)
      return compareScenarioValues(currentValues, scenarioValues)
    },
    [
      scenarioStrategy,
      rsf_a,
      e,
      y,
      w,
      s,
      tsl,
      fca,
      fca_ml,
      selectedMawpCalcThickness,
      calcType,
    ]
  )

  // When selectedScenarioId changes, update all the values from the selected scenario
  useEffect(() => {
    const selectedScenario = scenarios.find((scenario) =>
      scenarioStrategy.isSelectedScenario({
        scenario,
        selectedScenarioId,
        ffsLevel,
      })
    )
    const lastSelectedScenarioId = setLastSelectedScenarioId(selectedScenarioId)
    if (!selectedScenarioId) {
      return
    }
    if (selectedScenarioId !== lastSelectedScenarioId && selectedScenario) {
      setScenarioValues(selectedScenario)
    } else {
      if (!doesStateMatchScenario(selectedScenario)) {
        setSelectedScenarioId(null)
      }
    }
  }, [
    doesStateMatchScenario,
    ffsLevel,
    scenarioStrategy,
    scenarios,
    selectedScenarioId,
    setLastSelectedScenarioId,
    setScenarioValues,
    setSelectedScenarioId,
  ])
}
